import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import { nanoid } from 'nanoid';

import { makeStyles } from '@material-ui/core';
import ClientSelector from './ClientSelector';
import CartItem from './CartItem';
import { evaluateCartPrice, modifyItemQuantity, setCartError } from '../actions/cart';
import ButtonTransaction from './buttonTransaction';
import { displayAsPrice } from './helpers';
import TransactionRuleDisplay from './TransactionRule';

const useStyles = makeStyles(theme => ({
  paper: ({ isCompact }) => ({
    marginTop: theme.spacing(1),
    overflowX: 'auto',
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(1)}px`,
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    ...(!isCompact ? { marginBottom: theme.spacing(1) } : {}),
  }),
  mainContent: ({ isCompact }) => ({
    display: 'flex',
    flexDirection: 'column',
    ...(isCompact ? { flexGrow: 1, height: 0 } : {}),
  }),
  title: {
    marginBottom: theme.spacing(2),
  },
  noCreditWarning: {
    display: 'flex',
    height: '20px',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontWeight: 'bold',
    fontSize: 16,
    padding: theme.spacing(2),
  },
  itemContainer: ({ isCompact }) =>
    isCompact
      ? { overflowY: 'scroll', flex: '1 1 auto' }
      : {
          height: '400px',
          overflowY: 'scroll',
        },
  priceContainer: {
    padding: `${theme.spacing(3)}px 0`,
    height: '50px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  compactPriceContainer: {
    flexDirection: 'column-reverse',
    padding: theme.spacing(0.5),
    height: 'auto',
    '& > div:first-of-type, button': {
      width: '100%',
    },
  },
  button: {
    display: 'flex',
    flexDirection: 'column',
  },
  totalPrice: {
    color: theme.palette.primary.dark,
    fontWeight: 800,
    fontSize: '18px',
  },
}));

const Cart = ({
  items,
  totalPrice,
  transactionRules,
  showCartError,
  setSnackBarMessage,
  error,
  updateItemQuantity,
  loading,
  clientHasNoCredit,
  isCompact,
}) => {
  const classes = useStyles({ isCompact });

  useEffect(() => {
    if (error) {
      setSnackBarMessage(error);
      showCartError(null);
    }
  }, [error, setSnackBarMessage, showCartError]);

  const cartItems = items.filter(item => item.total.quantity > 0);

  return (
    <Paper className={classes.paper}>
      <div className={classes.mainContent}>
        <ClientSelector setSnackBarMessage={setSnackBarMessage} />
        <div className={classes.itemContainer}>
          {cartItems.length > 0 &&
            transactionRules.map(({ name, amount }) => (
              <TransactionRuleDisplay key={nanoid()} name={name} amount={amount} />
            ))}
          {cartItems.map(product => (
            <CartItem
              key={nanoid()}
              product={product}
              loading={loading}
              updateCartPrice={quantity => updateItemQuantity(product.id, quantity)}
              showCartError={showCartError}
              isCompact={isCompact}
            />
          ))}
        </div>
      </div>
      <div
        className={[
          classes.priceContainer,
          ...(isCompact ? [classes.compactPriceContainer] : []),
        ].join(' ')}
      >
        <ButtonTransaction clientHasNoCredit={clientHasNoCredit} />
        {clientHasNoCredit && <div className={classes.noCreditWarning}>Pas de crédit !</div>}
        <div aria-label="cart-total" className={classes.totalPrice}>
          TOTAL: {displayAsPrice(totalPrice)}
        </div>
      </div>
    </Paper>
  );
};

Cart.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      qty: PropTypes.number,
      price: PropTypes.number,
    }),
  ).isRequired,
  totalPrice: PropTypes.number.isRequired,
  transactionRules: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      quantity: PropTypes.number,
      price: PropTypes.number,
    }),
  ).isRequired,
  error: PropTypes.string,
  showCartError: PropTypes.func.isRequired,
  setSnackBarMessage: PropTypes.func.isRequired,
  updateItemQuantity: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  clientHasNoCredit: PropTypes.bool.isRequired,
  isCompact: PropTypes.bool.isRequired,
};

Cart.defaultProps = {
  error: null,
};

const mapStateToProps = state => {
  const { cart, client } = state;

  return {
    items: cart.items,
    warehouse: state.warehouse.content,
    totalPrice: cart.totalPrice,
    transactionRules: cart.transactionRules,
    error: cart.error,
    clientHasNoCredit: client.userId !== -1 && !client.canPurchase,
    loading: cart.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  showCartError: error => {
    dispatch(setCartError(error));
  },
  updateItemQuantity: (id, quantity) => {
    dispatch(modifyItemQuantity(id, quantity));
    dispatch(evaluateCartPrice());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
